import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import {
  GlobalMessageService,
  GlobalMessageType,
  TranslationService,
} from '@spartacus/core';
import { Observable, Subscription } from 'rxjs';
import { filter, first, map, switchMap, tap } from 'rxjs/operators';
import {
  CanonDetailsData,
  ProductCodeType,
  ReportChannel,
  ReportProductSupplier,
  ReportTransferStatus,
  ReportTransferType,
  SalesTransactionType,
  VendorReportTransferData,
  VendorReportingDetailData,
  VendorReportingDetailResponse,
  VendorReportingDetailResultsData,
  YESNO,
} from '../../custom-vendor-reporting.models';
import { CustomVendorReportingService } from '../../custom-vendor-reporting.service';
import {
  deleteVendorReportingDetails,
  deleteVendorReportingDetailsReset,
  getVendorReportingDetails,
  updateVendorReportingDetails,
  updateVendorReportingDetailsReset,
} from '../../store/custom-vendor-reporting.action';
import { CustomVendorReportingState } from '../../store/custom-vendor-reporting.reducer';
import {
  deleteVendorReportingDetailsFailure,
  deleteVendorReportingDetailsSuccess,
  errorVendorReportingDetails,
  selectVendorReportingDetails,
  updateVendorReportingDetailsFailure,
  updateVendorReportingDetailsFailureMessage,
  updateVendorReportingDetailsSuccess,
} from '../../store/custom-vendor-reporting.selector';


@Component({
    selector: 'app-custom-canon-vendor-reporting-details',
    templateUrl: './custom-canon-vendor-reporting-details.component.html',
    styleUrls: ['./custom-canon-vendor-reporting-details.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CustomCanonVendorReportingDetailsComponent
  implements OnInit, OnDestroy
{
  reportTransferData = this.vendorReportingService.getReportTransferData;
  isRioEmployee=this.vendorReportingService.getIsRioEmployee;
  productCodeTypeEnum = ProductCodeType;

  @ViewChild('editForm') editForm: NgForm;

  vendorReportDetails$: Observable<VendorReportingDetailResponse> =
    this.store.select(selectVendorReportingDetails);
  errorDetails$: Observable<Error> = this.store.select(
    errorVendorReportingDetails
  );

  deleteSuccess$: Observable<boolean> = this.store.select(
    deleteVendorReportingDetailsSuccess
  );
  deleteFailure$: Observable<boolean> = this.store.select(
    deleteVendorReportingDetailsFailure
  );
  updateSuccess$: Observable<boolean> = this.store.select(
    updateVendorReportingDetailsSuccess
  );
  updateFailure$: Observable<boolean> = this.store.select(
    updateVendorReportingDetailsFailure
  );
  updateFailureMessage$: Observable<VendorReportingDetailResultsData[]> =
    this.store.select(updateVendorReportingDetailsFailureMessage);

  vendorReportDetails: VendorReportingDetailResponse;
  vendorReportTransferData: VendorReportTransferData;
  entries: VendorReportingDetailData[];
  errorDetails: Error;

  reportChannelList = Object.values(ReportChannel).filter(
    (value) => typeof value === 'string'
  ) as string[];

  salesTransactionTypeList = Object.values(SalesTransactionType).filter(
    (value) => typeof value === 'string'
  ) as string[];

  yesNoList = Object.values(YESNO).filter(
    (value) => typeof value === 'string'
  ) as string[];

  reportProductSupplierList = Object.values(ReportProductSupplier).filter(
    (value) => typeof value === 'string'
  ) as string[];

  productCodeTypeList = Object.values(ProductCodeType).filter(
    (value) => typeof value === 'string'
  ) as string[];

  reportDataDetailsList: CanonDetailsData[] = [];
  deletedReportDataDetailsList: CanonDetailsData[] = [];
  newDetailData: CanonDetailsData;
  isEdit: boolean;
  isSalesData: boolean;
  branches: string[] = [];

  subscription = new Subscription();

  constructor(
    private modal: NgbActiveModal,
    private store: Store<CustomVendorReportingState>,
    private vendorReportingService: CustomVendorReportingService,
    private translation: TranslationService,
    private messageService: GlobalMessageService,
  ) {}

  ngOnInit(): void {
    this.initView();
    this.initDetailsData();
    this.getVendorReportDetails();
    this.loadBranchesDropdown();

    this.vendorReportDetails$
      .pipe(
        filter((vendorReportDetails) => vendorReportDetails != null),
        tap((vendorReportDetails) => {
          this.vendorReportDetails = vendorReportDetails;
          if (this.vendorReportDetails.responses.length > 0) {
            this.reportDataDetailsList = this.vendorReportDetails.responses.map(
              (detail) => ({ ...detail })
            );
          }
        })
      )
      .subscribe();


    this.errorDetails$
      .pipe(
        filter((error) => error != null),
        tap((error) => {
          this.errorDetails = error;
        }),
        switchMap(() =>
          this.translation.translate('customVendorReporting.details.error')
        ),
        tap((text) => {
          this.messageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
          this.modal.close();
        })
      )
      .subscribe();

    this.handleUpdateSuccess();
    this.subscription.add(this.handleUpdateError());
    this.handleDeletionSuccess();
    this.handleDeletionError();
  }

  initView() {
    this.isEdit =
      this.reportTransferData.reportTransferStatus ===
      ReportTransferStatus[ReportTransferStatus.Open] && !this.isRioEmployee
        ? true
        : false;
    this.isSalesData =
      this.reportTransferData.reportTransferType ===
      ReportTransferType[ReportTransferType.Sales]
        ? true
        : false;
  }

  initDetailsData() {
    if (this.isSalesData) {
      this.initSalesDetailData();
    } else {
      this.initInventoryData();
    }
  }

  private initSalesDetailData() {
    this.newDetailData = null;
    this.newDetailData = {
      quantity: 1,
      bundle: YESNO[YESNO.NO],
      transactionType: SalesTransactionType[SalesTransactionType.S],
      onlineSale:
        this.reportTransferData?.reportChannel ===
        ReportChannel[ReportChannel.Online]
          ? YESNO[YESNO.YES]
          : YESNO[YESNO.NO],
      productCodeType: ProductCodeType[ProductCodeType.H]

    };
  }

  private initInventoryData() {
    this.newDetailData = null;
    this.newDetailData = {
      quantity: 0,
      productCodeType: ProductCodeType[ProductCodeType.H],
      inventoryDate: this.reportTransferData.inventoryReportDate
    };
  }

  getVendorReportDetails() {
    this.store.dispatch(
      getVendorReportingDetails({
        reportTransferData: this.reportTransferData,
      })
    );
  }

  addDetail(detailData: CanonDetailsData) {
    this.reportDataDetailsList.push(detailData);
    this.initDetailsData();
  }

  updateDetail(detail: CanonDetailsData) {
    if (!detail.pk || detail.pk.length === 0) {
      this.populateProductArticleNumberFields(detail);
      return;
    }
    detail.updated = true;
  }

  deleteDetail(detail: CanonDetailsData, index: number) {
    if (detail.pk) {
      this.deletedReportDataDetailsList.push(detail);
    }
    this.reportDataDetailsList.splice(index, 1);
  }

  populateProductArticleNumberFields(detail: CanonDetailsData){
    switch (detail.productCodeType) {
      case ProductCodeType[ProductCodeType.E]:
        detail.manufacturerPartNumber = undefined;
        detail.productCode = undefined;
        break;
      case ProductCodeType[ProductCodeType.H]:
        detail.gtin = undefined;
        detail.productCode = undefined;
        break;
      default:
        detail.manufacturerPartNumber = undefined;
        detail.gtin = undefined;
    }
  }

  isSaveDisabled(editForm: NgForm) {
    if (this.reportDataDetailsList.find(detail => !detail.pk)) {
      return !editForm.valid;
    }
    return (this.deletedReportDataDetailsList.length < 1 && editForm.untouched) || (editForm.dirty && !editForm.valid) || (this.reportDataDetailsList.length < 1 && this.deletedReportDataDetailsList.length < 1);
  }

  saveChanges() {
    if (
      this.editForm.pristine &&
      this.reportDataDetailsList.length < 1 &&
      this.deletedReportDataDetailsList.length < 1
    ) {
      this.backToOverview();
      return;
    }
    this.sendSaveRequest();
    this.sendDeleteRequest();
  }

  sendSaveRequest() {
    if (this.reportDataDetailsList.length < 1) {
      return;
    }
    this.store.dispatch(
      updateVendorReportingDetails({
        reportTransferData: this.reportTransferData,
        VendorReportingDetailsList: this.reportDataDetailsList,
      })
    );
  }

  sendDeleteRequest() {
    if (this.deletedReportDataDetailsList.length < 1) {
      return;
    }
    this.store.dispatch(
      deleteVendorReportingDetails({
        reportTransferData: this.reportTransferData,
        VendorReportingDetailsList: this.deletedReportDataDetailsList,
      })
    );
  }

  handleDeletionSuccess() {
    this.deleteSuccess$
      .pipe(
        filter((success) => success == true),
        map(() => {
          this.translation
            .translate('customVendorReporting.messages.deleteSuccess')
            .pipe(first())
            .subscribe((text) => {
              this.messageService.add(
                text,
                GlobalMessageType.MSG_TYPE_CONFIRMATION
              );
              this.store.dispatch(deleteVendorReportingDetailsReset());
              this.modal.close();
            });
        })
      )
      .subscribe();
  }

  handleDeletionError() {
    this.deleteFailure$
      .pipe(
        filter((failure) => failure && failure == true),
        tap(() => {
          this.translation
            .translate('customVendorReporting.messages.deleteFailure')
            .pipe(first())
            .subscribe((text) => {
              this.messageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
              this.store.dispatch(deleteVendorReportingDetailsReset());
              this.backToOverview();
            });
        })
      )
      .subscribe();
  }

  handleUpdateSuccess() {
    this.updateSuccess$
      .pipe(
        filter((success) => success && success == true),
        map(() => {
          this.translation
            .translate('customVendorReporting.messages.saveSuccess')
            .pipe(first())
            .subscribe((text) => {
              this.messageService.add(
                text,
                GlobalMessageType.MSG_TYPE_CONFIRMATION
              );
              this.store.dispatch(updateVendorReportingDetailsReset());
              this.backToOverview();
            });
        })
      )
      .subscribe();
  }

  handleUpdateError() {
    let errorMessages: VendorReportingDetailResultsData[] = [];
    return this.updateFailure$
      .pipe(
        filter((failure) => failure && failure == true),
        tap(() => {
          this.updateFailureMessage$
            .pipe(
              filter((messages) => messages != null && messages.length > 0),
              tap<VendorReportingDetailResultsData[]>((messages) => {
                errorMessages = messages;
              })
            )
            .subscribe();
          const SHOW_MSG_TIMEOUT_INFO = 600000; // 10 minutes

          for (const errorMessage of errorMessages) {
            this.translation
              .translate(
                'customVendorReporting.messages.' + errorMessage.messageKey,
                { productCode: errorMessage.productCode,
                  inventoryDate: errorMessage.inventoryDate
                }
              )
              .pipe(first())
              .subscribe((text) => {
                this.messageService.add(
                  text,
                  GlobalMessageType.MSG_TYPE_ERROR,
                  SHOW_MSG_TIMEOUT_INFO
                );
              });
          }
          this.store.dispatch(updateVendorReportingDetailsReset());
          this.backToOverview();
        })
      )
      .subscribe();
  }

  toggleEdit() {
    this.isEdit = !this.isEdit;
  }

  private backToOverview() {
    this.modal.close();
    document.getElementById('vendor-reporting-filter').scrollIntoView();
  }

  private loadBranchesDropdown() {
    this.subscription.add(this.vendorReportingService.getBranchesList().subscribe((branches)  => {
      this.branches = branches.map(b => b.branchId)
    }));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
